import { Controller } from "@hotwired/stimulus"

function popupOpen() {
  if (document.querySelector("dialog[open], [role='menu']:not(.hidden):not([popover])")) return true
  try {
    return Boolean(document.querySelector(":popover-open"))
  } catch {
    // Browsers without the Popover API reject the selector and have no open popovers.
    return false
  }
}

export default class extends Controller {
  static targets = ["form", "bulkAction", "bulkPanel", "selectedCount", "setAllCheckbox", "totalCheckboxes", "columns", "filters"]

  connect() {
    if (!this.hasSetAllCheckboxTarget) return

    if (!this.element.closest("[data-custom-row-selection]")) this.setupRowShift()
    this.bulkActionTargets.forEach(element => {
      element.addEventListener("click", this.performBulkAction.bind(this))
    });

    this.setAllCheckboxTarget.addEventListener("change", this.setAllCheckboxes.bind(this))

    this.totalCheckboxesTarget.innerText = this.element.querySelectorAll("input[type='checkbox'][name='id[]']").length
    this.element.querySelectorAll("input[type='checkbox']").forEach(checkbox => {
      checkbox.addEventListener("change", this.updateSelectedCheckboxes.bind(this))
    })
    this.allCheckboxes = this.element.querySelectorAll("input[type='checkbox'][name='id[]']")

    this.keydown = event => this.handleKey(event)
    this.morphed = () => this.refreshed()
    this.submitEnd = event => this.handleSubmitEnd(event)
    // Capture phase: clearing the ticks comes before anything else Escape would do on the page.
    document.addEventListener("keydown", this.keydown, true)
    document.addEventListener("turbo:morph", this.morphed)
    document.addEventListener("turbo:submit-end", this.submitEnd)

    // Listen for Turbo Stream updates to reset checkbox states
    this.element.addEventListener('turbo:stream-render', this.handleTurboStreamUpdate.bind(this))
  }

  disconnect() {
    if (!this.keydown) return

    document.removeEventListener("keydown", this.keydown, true)
    document.removeEventListener("turbo:morph", this.morphed)
    document.removeEventListener("turbo:submit-end", this.submitEnd)
  }

  setupRowShift() {
    // Prevent native checkbox toggle when shift-clicking directly on checkboxes
    // This must be in capture phase to run before the checkbox's native behavior
    this.element.querySelectorAll("input[type='checkbox'][name='id[]']").forEach(checkbox => {
      checkbox.addEventListener("click", e => {
        if (e.shiftKey && this.lastClickedRow) {
          e.preventDefault()
        }
      }, true) // capture phase
    })

    this.element.querySelectorAll("tbody tr").forEach(row => {
      row.addEventListener("click", e => {
        var currentRow = e.target
        if (currentRow.tagName !== "TR") currentRow = e.target.closest("tr")

        if (e.shiftKey && this.lastClickedRow) {
          e.preventDefault()
          const allRows = Array.from(this.element.querySelectorAll("tbody tr"))
          const lastIndex = allRows.indexOf(this.lastClickedRow)
          const currentIndex = allRows.indexOf(currentRow)

          const [start, end] = lastIndex < currentIndex
            ? [lastIndex, currentIndex]
            : [currentIndex, lastIndex]

          allRows.slice(start, end + 1).forEach(r => {
            const checkbox = r.querySelector("input[type='checkbox'][name='id[]']")
            checkbox.checked = true
          })
          this.updateSelectedCheckboxes()
        }

        this.lastClickedRow = currentRow
      })
    })
  }

  openColumns() {
    this.openDialog(this.columnsTarget.querySelector("dialog"))
  }

  openFilters() {
    this.openDialog(this.filtersTarget.querySelector("dialog"))
  }

  openDialog(dialog) {
    dialog?.showModal()
    dialog?.focus()
  }

  updateSelectedCheckboxes(event) {
    var selectedCheckboxes = this.selectedCount()

    // Update the indeterminate state and checked state of the "set all" checkbox
    const totalCheckboxes = this.allCheckboxes.length
    if (selectedCheckboxes === 0) {
      this.setAllCheckboxTarget.indeterminate = false
      this.setAllCheckboxTarget.checked = false
    } else if (selectedCheckboxes === totalCheckboxes) {
      this.setAllCheckboxTarget.indeterminate = false
      this.setAllCheckboxTarget.checked = true
    } else {
      this.setAllCheckboxTarget.indeterminate = true
      this.setAllCheckboxTarget.checked = false
    }
    
    this.toggleBulkPanel(selectedCheckboxes)
  }

  selectedCount() {
    return Array.from(this.allCheckboxes).filter(checkbox => checkbox.checked).length
  }

  performBulkAction(event) {
    const button = event.currentTarget

    if (!button.dataset.turboConfirm || confirm(button.dataset.turboConfirm)) {
      const path = button.dataset.path
      const httpMethod = button.dataset.httpMethod

      this.formTarget.querySelector(`input[name="_method"]`).value = httpMethod
      this.formTarget.action = path

      this.formTarget.requestSubmit()
    }
  }

  // An action is done when it redirects back here, from the list form or a dialog it opened. Opening a dialog does not
  // redirect, so cancelling one keeps the selection. One lead on a page with its own selection moves on to the next lead.
  handleSubmitEnd(event) {
    const form = event.target
    if (!event.detail?.success || !event.detail.fetchResponse?.redirected) return
    if (form !== this.formTarget && !form.closest?.("dialog")) return
    if (this.element.closest("[data-custom-row-selection]") && this.selectedCount() < 2) return

    this.resetCheckboxStates()
  }

  setAllCheckboxes(event) {
    this.formTarget.querySelectorAll("input[type='checkbox']").forEach(checkbox => {
      checkbox.checked = event.currentTarget.checked
    })

    // Reset indeterminate state when "set all" is clicked
    this.setAllCheckboxTarget.indeterminate = false
    
    this.toggleBulkPanel(event.currentTarget.checked ? this.allCheckboxes.length : 0)
  }

  toggleBulkPanel(selectedCount) {
    this.element.dispatchEvent(new CustomEvent("list:selection-changed", { bubbles: true, detail: { count: selectedCount, morph: Boolean(this.morphing) } }))
    if (!this.hasBulkPanelTarget) return

    this.bulkPanelTarget.hidden = selectedCount === 0
    this.selectedCountTarget.textContent = this.selectedCountTarget.dataset.label.replace("__count__", selectedCount)
  }

  clearSelection() {
    this.resetCheckboxStates()
  }

  // Escape clears the ticks first. Open dialogs, menus and text fields keep Escape for themselves.
  handleKey(event) {
    if (event.key !== "Escape" || event.defaultPrevented || this.selectedCount() === 0) return
    if (event.target.closest?.("input:not([type='checkbox']), textarea, select, [contenteditable]")) return
    if (popupOpen()) return

    event.preventDefault()
    this.clearSelection()
  }

  // A morph refresh resets the bar to the server's hidden state but can keep ticked checkboxes, so match them again.
  refreshed() {
    this.allCheckboxes = this.element.querySelectorAll("input[type='checkbox'][name='id[]']")
    this.totalCheckboxesTarget.innerText = this.allCheckboxes.length
    this.morphing = true
    this.updateSelectedCheckboxes()
    this.morphing = false
  }

  handleTurboStreamUpdate(event) {
    // Reset all checkbox states after Turbo Stream update
    this.resetCheckboxStates()
  }

  resetCheckboxStates() {
    // Uncheck all checkboxes
    this.allCheckboxes = this.element.querySelectorAll("input[type='checkbox'][name='id[]']")
    this.allCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })
    
    // Manually trigger the updateSelectedCheckboxes logic to properly sync master checkbox
    this.updateSelectedCheckboxes({})
  }
}
